import { useState, useEffect, useRef } from "react";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Skeleton from "@mui/material/Skeleton";
import { useFormContext } from "react-hook-form";
import { useDebounced } from "../../../../hooks/useDebounce";
import { META } from "../../../../utils/constants";
import { useDispatchApp } from "../../../../lib/redux";
import { startGetEstimationReservationAdsCampaign } from "../../../../actions/adsCampaigns";
import { numberCompactFormat } from "../../../../utils/numbers";
import { getCurrencyAdAccountPlatform } from "../../../../actions/getters";
import {
  CPM_METRICS,
  ESTIMATED_AUDIENCE_SIZE,
  FIELD_ESTIMATION_BUDGET_REQUIRED,
  FIELD_ESTIMATION_FREQUENCY_REQUIRED,
  FIELD_ESTIMATION_LOCATION_REQUIRED,
  FIELD_ESTIMATION_START_END_DATE_REQUIRED,
  REACH,
  REACH_AT_TARGET_FREQUENCY,
  RESERVATION_ESTIMATES,
  RESERVATION_STATUS_1,
  RESERVATION_STATUS_2,
  RESERVATION_STATUS_3,
  RESERVATION_STATUS_4,
  RESERVATION_STATUS_5,
  RESERVATION_STATUS_6,
  RESERVATION_STATUS_7,
  RESERVATION_STATUS_8,
  RESERVATION_STATUS_9,
  RESERVATION_STATUS_10,
  RESERVATION_STATUS_11,
  RESERVATION_STATUS_12,
  RESERVATION_STATUS_13,
  RESERVATION_STATUS_14,
  RESERVATION_STATUS_15,
  RESERVATION_STATUS_16,
  RESERVATION_STATUS_17,
  RESERVATION_STATUS_18,
  RESERVATION_STATUS_19,
  RESERVATION_STATUS_20,
  RESERVATION_STATUS_21,
  RESERVATION_STATUS_23,
  RESERVATION_STATUS_24,
  RESERVATION_STATUS_25,
  RESERVATION_STATUS_26,
  RESERVATION_STATUS_27,
  RESERVATION_STATUS_28,
  RESERVATION_STATUS_29,
  RESERVATION_STATUS_30,
  RESERVATION_STATUS_31,
  RESERVATION_STATUS_32,
  RESERVATION_STATUS_35,
  RESERVATION_STATUS_36,
  RESERVATION_STATUS_37,
  RESERVATION_STATUS_39,
  RESERVATION_STATUS_40,
  RESERVATION_STATUS_41,
  RESERVATION_STATUS_42,
  RESERVATION_STATUS_44,
  RESERVATION_STATUS_45,
  RESERVATION_STATUS_50,
  RESERVATION_STATUS_53,
  RESERVATION_STATUS_60,
  RESERVATION_STATUS_66,
  RESERVATION_STATUS_69,
  RESERVATION_STATUS_100,
} from "../../../../i18n/keysTranslations";
import { useTranslationApp } from "../../../../lib/i18next";
import _ from "lodash";
import { getReactHookFormNestedError } from "../../../../utils/errors";
import CardContainer from "../../../Card/CardContainer";

const getReservationStatusKey = (status) => {
  // Map status number to corresponding translation key
  const statusMap = {
    1: RESERVATION_STATUS_1,
    2: RESERVATION_STATUS_2,
    3: RESERVATION_STATUS_3,
    4: RESERVATION_STATUS_4,
    5: RESERVATION_STATUS_5,
    6: RESERVATION_STATUS_6,
    7: RESERVATION_STATUS_7,
    8: RESERVATION_STATUS_8,
    9: RESERVATION_STATUS_9,
    10: RESERVATION_STATUS_10,
    11: RESERVATION_STATUS_11,
    12: RESERVATION_STATUS_12,
    13: RESERVATION_STATUS_13,
    14: RESERVATION_STATUS_14,
    15: RESERVATION_STATUS_15,
    16: RESERVATION_STATUS_16,
    17: RESERVATION_STATUS_17,
    18: RESERVATION_STATUS_18,
    19: RESERVATION_STATUS_19,
    20: RESERVATION_STATUS_20,
    21: RESERVATION_STATUS_21,
    23: RESERVATION_STATUS_23,
    24: RESERVATION_STATUS_24,
    25: RESERVATION_STATUS_25,
    26: RESERVATION_STATUS_26,
    27: RESERVATION_STATUS_27,
    28: RESERVATION_STATUS_28,
    29: RESERVATION_STATUS_29,
    30: RESERVATION_STATUS_30,
    31: RESERVATION_STATUS_31,
    32: RESERVATION_STATUS_32,
    35: RESERVATION_STATUS_35,
    36: RESERVATION_STATUS_36,
    37: RESERVATION_STATUS_37,
    39: RESERVATION_STATUS_39,
    40: RESERVATION_STATUS_40,
    41: RESERVATION_STATUS_41,
    42: RESERVATION_STATUS_42,
    44: RESERVATION_STATUS_44,
    45: RESERVATION_STATUS_45,
    50: RESERVATION_STATUS_50,
    53: RESERVATION_STATUS_53,
    60: RESERVATION_STATUS_60,
    66: RESERVATION_STATUS_66,
    69: RESERVATION_STATUS_69,
    100: RESERVATION_STATUS_100,
  };

  return statusMap[status] || null;
};

const ReservationEstimatesPreview = ({ BASIC_PATH }) => {
  const {
    watch,
    getValues,
    setValue,
    formState: { errors },
  } = useFormContext();
  const abortControllerRef = useRef(null);

  const [isLoading, setIsLoading] = useState(true);
  const [isFirstLoading, setIsFirstLoading] = useState(true);
  const [estimationData, setEstimationData] = useState({
    audienceSize: "",
    reach: "",
    cpm: "",
    reactTargetFrequency: "",
  });
  const [errorGenerateEstimation, setErrorGenerateEstimation] = useState(null);

  // Watch form data
  const budget = watch(`${BASIC_PATH}.budget`);
  const startDate = watch(`${BASIC_PATH}.startDate`);
  const endDate = watch(`${BASIC_PATH}.endDate`);
  const locations = watch(`${BASIC_PATH}.locations`);
  const genders = watch(`${BASIC_PATH}.genders`);
  const ageRange = watch(`${BASIC_PATH}.ageRange`);
  const interests = watch(`${BASIC_PATH}.interests`);
  const behaviors = watch(`${BASIC_PATH}.behaviors`);
  const demographics = watch(`${BASIC_PATH}.demographics`);
  const customAudiences = watch(`${BASIC_PATH}.customAudiences`);
  const excludedCustomAudiences = watch(
    `${BASIC_PATH}.excludedCustomAudiences`
  );
  const frequency = watch(`${BASIC_PATH}.frequency`);
  const estimationID = watch(`${BASIC_PATH}.estimationID`);

  const platformData = getValues(`platformsData.${META}`);
  const integrationID = platformData?.integrationID;
  const adAccountID = platformData?.adAccountID;
  const pageID = platformData?.pageID;

  const dispatch = useDispatchApp();
  const { t } = useTranslationApp();
  const error = getReactHookFormNestedError(
    errors,
    `${BASIC_PATH}.estimationID`
  );

  const currency = dispatch(
    getCurrencyAdAccountPlatform({
      platform: META,
      integrationID,
      adAccountID,
    })
  );

  // Simulate API call
  const simulateEstimationRequest = async (type) => {
    let newEstimationID = estimationID;
    if (type === "change") {
      newEstimationID = "";
    }
    if (type === "first") {
      setIsFirstLoading(false);
    }

    // Abort previous request if exists
    if (abortControllerRef?.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    setIsLoading(true);

    const response = await dispatch(
      startGetEstimationReservationAdsCampaign(
        {
          integrationID,
          adAccountID,
          estimationID: newEstimationID,
          pageID,
          budget,
          startDate,
          endDate,
          locations,
          genders,
          ageRange,
          interests,
          behaviors,
          demographics,
          customAudiences,
          excludedCustomAudiences,
          frequency,
        },
        abortControllerRef.current.signal
      )
    );

    if (!response.ok) {
      if (response.aborted) {
        setIsLoading(false);
        return;
      }
    }

    if (response.ok) {
      const data = response.data;

      // Generate mock data based on form values
      const newEstimationData = {
        audienceSize: `${numberCompactFormat(data.minSize || 0)} - ${numberCompactFormat(data.maxSize || 0)}`,
        reach: numberCompactFormat(data.reach || 0),
        cpm: `${data.cpm || 0} ${currency}`,
        reactTargetFrequency: `${data.reactTargetFrequency || 0}%`,
        status: data.status,
        reservationStatus: data.status,
      };
      setValue(`${BASIC_PATH}.estimationID`, data.id, {
        shouldValidate: true,
        shouldDirty: true,
      });
      setEstimationData(newEstimationData);

      if (data.status >= 3) {
        setErrorGenerateEstimation(t(getReservationStatusKey(data.status)));
      }
    }

    setIsLoading(false);
  };

  // Debounced function
  const debouncedEstimationRequest = useDebounced(
    simulateEstimationRequest,
    5000
  );

  // Effect to trigger estimation when data changes
  useEffect(() => {
    if (!budget || budget < 0) {
      setErrorGenerateEstimation(t(FIELD_ESTIMATION_BUDGET_REQUIRED));
      return;
    }
    if (!startDate || !endDate) {
      setErrorGenerateEstimation(t(FIELD_ESTIMATION_START_END_DATE_REQUIRED));
      return;
    }

    if (!frequency?.value || !frequency?.interval) {
      setErrorGenerateEstimation(t(FIELD_ESTIMATION_FREQUENCY_REQUIRED));
      return;
    }

    if (_.isEmpty(locations)) {
      setErrorGenerateEstimation(t(FIELD_ESTIMATION_LOCATION_REQUIRED));
      return;
    }
    setErrorGenerateEstimation(null);

    // Abort previous debounced request if exists
    if (abortControllerRef?.current) {
      abortControllerRef.current.abort();
    }

    debouncedEstimationRequest(isFirstLoading ? "first" : "change");

    // eslint-disable-next-line
  }, [
    budget,
    startDate,
    endDate,
    locations,
    genders,
    ageRange,
    interests,
    behaviors,
    demographics,
    customAudiences,
    excludedCustomAudiences,
    frequency?.value,
    frequency?.interval,
    frequency?.type,
  ]);

  // Cleanup effect to abort requests on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef?.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return (
    <CardContainer
      sx={{
        mb: 2,
        maxHeight: "calc(100vh - 60px)",
      }}
    >
      <Typography
        variant="h6"
        gutterBottom
        sx={{
          fontWeight: 700,
        }}
      >
        {t(RESERVATION_ESTIMATES)}
      </Typography>
      {error && (
        <Typography
          variant="body2"
          color="error"
          sx={{ mb: 1 }}
          id={`${BASIC_PATH}.estimationID`}
        >
          {error.message}
        </Typography>
      )}
      {errorGenerateEstimation && (
        <Typography
          variant="body2"
          color="error"
          sx={{ mb: 1 }}
          id={`${BASIC_PATH}.estimationID`}
        >
          {errorGenerateEstimation}
        </Typography>
      )}

      {/* Metrics Grid */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 2,
          mb: 3,
          pt: 3,
        }}
      >
        <Box>
          <Typography variant="body2">{t(ESTIMATED_AUDIENCE_SIZE)}</Typography>
          {isLoading ? (
            <Skeleton variant="text" width="60%" height={24} />
          ) : (
            <Typography variant="body1">
              {estimationData.audienceSize}
            </Typography>
          )}
        </Box>
        {/* Reach */}
        <Box>
          <Typography variant="body2">{t(REACH)}</Typography>
          {isLoading ? (
            <Skeleton variant="text" width="50%" height={24} />
          ) : (
            <Typography variant="body1">{estimationData.reach}</Typography>
          )}
        </Box>

        {/* CPM */}
        <Box>
          <Typography variant="body2">{t(CPM_METRICS)}</Typography>
          {isLoading ? (
            <Skeleton variant="text" width="24%" height={24} />
          ) : (
            <Typography variant="body1">{estimationData.cpm}</Typography>
          )}
        </Box>

        {/* Reach at Target Frequency */}
        <Box>
          <Typography variant="body2">
            {t(REACH_AT_TARGET_FREQUENCY)}
          </Typography>
          {isLoading ? (
            <Skeleton variant="text" width="35%" height={24} />
          ) : (
            <Typography variant="body1">
              {estimationData.reactTargetFrequency}
            </Typography>
          )}
        </Box>
      </Box>
    </CardContainer>
  );
};

export default ReservationEstimatesPreview;
